// ErrorBoundary — wraps each federated Suspense so a remote that
// fails to load (404 on the remoteEntry.js, JS exception at render,
// etc.) shows a recoverable fallback instead of a blank screen.
//
// React function components can't catch errors in descendants; this
// has to be a class. The retry resets local state so the next render
// re-attempts the child tree — for federation specifically that means
// re-importing the remote chunk, which is what we want when an admin
// just pushed a new version and the old chunk hash 404s.
//
// We also log to console.error so any future error-reporting
// integration (Sentry, Honeybadger) sees the crash without needing
// surgery on every consumer.

import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "@sms/design-system";
import type { Locale } from "@sms/i18n";

import { makeT } from "./i18n";

interface Props {
  /** Locale for the fallback copy. */
  locale: Locale;
  children: ReactNode;
  /**
   * Optional callback fired with the captured error. Tests assert
   * shape; in production a logging adapter can forward to a remote.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console — defence-in-depth so the
    // error is visible even before a reporter is wired.
    console.error("boundary captured:", error, info);
    this.props.onError?.(error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      const t = makeT(this.props.locale);
      return (
        <div role="alert" style={containerStyle} data-testid="boundary-fallback">
          <strong style={titleStyle}>{t("common.boundary_title")}</strong>
          <p style={helpStyle}>{t("common.boundary_help")}</p>
          <Button onClick={this.reset}>{t("common.boundary_retry")}</Button>
        </div>
      );
    }
    return this.props.children;
  }
}

const containerStyle: React.CSSProperties = {
  margin: 16,
  padding: 16,
  borderRadius: 8,
  background: "#fee2e2",
  border: "1px solid #fecaca",
  fontFamily: '"Noto Sans Devanagari","Inter",system-ui,sans-serif',
};
const titleStyle: React.CSSProperties = {
  display: "block",
  fontSize: 16,
  color: "#991b1b",
  marginBottom: 4,
};
const helpStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#7f1d1d",
  margin: "0 0 12px",
};
